import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Wallet, Plus, Landmark, Coins, ArrowUpRight, ArrowDownRight, Trash2, Edit3,
  TrendingUp, TrendingDown,
} from 'lucide-react';
import ErpShell from '../components/ErpShell';
import { ErpPageHeader, ErpEmpty } from '../components/ErpPageHeader';
import { api, useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const fmtMoney = (v) => `₪${(Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return String(iso).slice(0, 10); }
};

const CATEGORIES = ['Sales', 'Customs Fee', 'Freight', 'Utilities', 'Salary', 'Rent', 'Fuel', 'Office', 'Other'];

const emptyForm = () => ({
  accountType: 'bank',
  transactionType: 'income',
  amount: '',
  description: '',
  category: 'Sales',
  notes: '',
  date: new Date().toISOString().slice(0, 10),
});

export default function ErpFinance() {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'ACCOUNTANT';
  const [tab, setTab] = useState('bank');
  const [bank, setBank] = useState({ transactions: [], balance: 0 });
  const [cash, setCash] = useState({ transactions: [], balance: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        api.get('/financial-tracker/bank').catch(() => ({ data: { transactions: [], balance: 0 } })),
        api.get('/financial-tracker/cash').catch(() => ({ data: { transactions: [], balance: 0 } })),
      ]);
      setBank(b.data);
      setCash(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const current = tab === 'bank' ? bank : cash;
  const other = tab === 'bank' ? cash : bank;

  const monthSummary = useMemo(() => {
    const now = new Date();
    const key = now.toISOString().slice(0, 7);
    const all = [...bank.transactions, ...cash.transactions];
    let income = 0, expense = 0;
    all.forEach((t) => {
      const d = (t.date || t.createdAt || '').toString().slice(0, 7);
      if (d !== key) return;
      const amt = Number(t.amount || 0);
      if (t.transactionType === 'income') income += amt;
      else expense += amt;
    });
    return { income, expense, profit: income - expense };
  }, [bank, cash]);

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      toast({ title: 'Amount required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, accountType: tab, amount: Number(form.amount) };
      if (editingId) {
        await api.put(`/financial-tracker/${editingId}`, payload);
        toast({ title: 'Transaction updated' });
      } else {
        await api.post('/financial-tracker', payload);
        toast({ title: 'Transaction added' });
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm());
      load();
    } catch { toast({ title: 'Failed to save', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await api.delete(`/financial-tracker/${id}`);
      toast({ title: 'Deleted' });
      load();
    } catch { toast({ title: 'Failed to delete', variant: 'destructive' }); }
  };

  const openEdit = (t) => {
    setForm({
      accountType: t.accountType || tab,
      transactionType: t.transactionType || 'income',
      amount: t.amount || '',
      description: t.description || '',
      category: t.category || 'Sales',
      notes: t.notes || '',
      date: (t.date || '').slice(0, 10) || new Date().toISOString().slice(0, 10),
    });
    setEditingId(t.id);
    setShowForm(true);
  };

  return (
    <ErpShell title="Finance">
      <ErpPageHeader
        eyebrow="Money"
        title="Finance"
        subtitle="Bank of Palestine & Office Cash — income, expenses, profit."
        icon={Wallet}
        actions={
          isAdmin && (
            <button onClick={() => { setForm(emptyForm()); setEditingId(null); setShowForm(true); }} className="gg-btn-primary" data-testid="new-txn-btn">
              <Plus size={14} /> Add Transaction
            </button>
          )
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <BalanceCard icon={Landmark} tone="info" label="Bank of Palestine" value={fmtMoney(bank.balance || 0)} sub={`${bank.transactions.length} entries`} onClick={() => setTab('bank')} active={tab === 'bank'} testid="bank-card" />
        <BalanceCard icon={Coins} tone="emerald" label="Office Cash" value={fmtMoney(cash.balance || 0)} sub={`${cash.transactions.length} entries`} onClick={() => setTab('cash')} active={tab === 'cash'} testid="cash-card" />
        <SummaryCard tone="emerald" icon={TrendingUp} label="This month income" value={fmtMoney(monthSummary.income)} />
        <SummaryCard tone={monthSummary.profit >= 0 ? 'accent' : 'danger'} icon={monthSummary.profit >= 0 ? TrendingUp : TrendingDown} label="Net profit this month" value={fmtMoney(monthSummary.profit)} />
      </div>

      {/* Tab switcher */}
      <div className="flex gap-2 mb-4">
        {[
          { key: 'bank', label: 'Bank of Palestine', icon: Landmark },
          { key: 'cash', label: 'Office Cash', icon: Coins },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={tab === t.key ? 'gg-btn-primary' : 'gg-btn-secondary'}
            data-testid={`finance-tab-${t.key}`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="gg-card p-12 animate-pulse" />
      ) : current.transactions.length === 0 ? (
        <ErpEmpty
          icon={Wallet}
          title={`No transactions in ${tab === 'bank' ? 'Bank of Palestine' : 'Office Cash'} yet`}
          subtitle="Log incomes and expenses to track your profit."
          action={isAdmin && <button onClick={() => setShowForm(true)} className="gg-btn-primary"><Plus size={14} /> Add first transaction</button>}
        />
      ) : (
        <div className="gg-card overflow-hidden" data-testid={`${tab}-table`}>
          <div className="overflow-x-auto">
            <table className="gg-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th className="text-end">Amount</th>
                  {isAdmin && <th style={{ width: 80 }}></th>}
                </tr>
              </thead>
              <tbody>
                {current.transactions.map((t, idx) => (
                  <motion.tr
                    key={t.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: Math.min(idx * 0.02, 0.3) }}
                    data-testid={`txn-row-${t.id}`}
                  >
                    <td className="gg-tabular" style={{ color: 'var(--gg-text-dim)' }}>{fmtDate(t.date || t.createdAt)}</td>
                    <td>
                      <span className={`gg-chip ${t.transactionType === 'income' ? 'gg-chip-emerald' : 'gg-chip-danger'}`}>
                        {t.transactionType === 'income' ? <ArrowDownRight size={11} /> : <ArrowUpRight size={11} />}
                        {t.transactionType}
                      </span>
                    </td>
                    <td className="font-medium max-w-xs truncate">{t.description || '—'}</td>
                    <td><span className="gg-chip gg-chip-neutral">{t.category}</span></td>
                    <td className="text-end gg-tabular font-semibold" style={{ color: t.transactionType === 'income' ? 'var(--gg-emerald)' : 'var(--gg-danger)' }}>
                      {t.transactionType === 'income' ? '+' : '−'} {fmtMoney(t.amount)}
                    </td>
                    {isAdmin && (
                      <td>
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => openEdit(t)} className="gg-btn-ghost" data-testid={`edit-txn-${t.id}`}><Edit3 size={13} /></button>
                          <button onClick={() => remove(t.id)} className="gg-btn-ghost" style={{ color: 'var(--gg-danger)' }} data-testid={`delete-txn-${t.id}`}><Trash2 size={13} /></button>
                        </div>
                      </td>
                    )}
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="gg-cmdk-overlay flex items-start justify-center pt-20" onClick={() => !saving && setShowForm(false)}>
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="gg-cmdk-panel p-6"
            style={{ width: 'min(560px, 92vw)' }}
            data-testid="txn-form"
          >
            <p className="gg-eyebrow" style={{ color: 'var(--gg-accent)' }}>{editingId ? 'Edit' : 'New'}</p>
            <h2 className="gg-hero-title" style={{ fontSize: 22 }}>Transaction · {tab === 'bank' ? 'Bank' : 'Cash'}</h2>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div>
                <label className="gg-eyebrow block mb-1">Type</label>
                <select value={form.transactionType} onChange={(e) => setForm({ ...form, transactionType: e.target.value })} className="gg-input" data-testid="txn-type">
                  <option value="income">Income</option>
                  <option value="expense">Expense</option>
                </select>
              </div>
              <div>
                <label className="gg-eyebrow block mb-1">Amount (₪) *</label>
                <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="gg-input" data-testid="txn-amount" />
              </div>
              <div className="col-span-2">
                <label className="gg-eyebrow block mb-1">Description</label>
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="gg-input" data-testid="txn-description" />
              </div>
              <div>
                <label className="gg-eyebrow block mb-1">Category</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="gg-input" data-testid="txn-category">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="gg-eyebrow block mb-1">Date</label>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="gg-input" data-testid="txn-date" />
              </div>
              <div className="col-span-2">
                <label className="gg-eyebrow block mb-1">Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="gg-input" rows={2} style={{ height: 'auto', padding: '10px 14px' }} data-testid="txn-notes" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowForm(false)} disabled={saving} className="gg-btn-secondary" data-testid="txn-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="gg-btn-primary" data-testid="txn-save">
                {saving ? 'Saving…' : editingId ? 'Update' : 'Save'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </ErpShell>
  );
}

const BalanceCard = ({ icon: Icon, tone, label, value, sub, onClick, active, testid }) => (
  <motion.button
    whileHover={{ y: -3 }}
    onClick={onClick}
    className="text-start gg-card gg-card-hover p-5 relative overflow-hidden"
    data-testid={testid}
    style={active ? { borderColor: 'var(--gg-accent)', boxShadow: 'var(--gg-shadow-elev)' } : {}}
  >
    <div
      className="absolute -top-10 -end-10 w-32 h-32 rounded-full opacity-40 pointer-events-none"
      style={{
        background: tone === 'emerald'
          ? 'radial-gradient(circle, rgba(52,199,89,0.4), transparent 70%)'
          : 'radial-gradient(circle, rgba(0,122,255,0.4), transparent 70%)',
        filter: 'blur(24px)',
      }}
    />
    <div className="flex items-start gap-3 relative">
      <span className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0`} style={{ background: tone === 'emerald' ? 'var(--gg-emerald-soft)' : 'var(--gg-info-soft)', color: tone === 'emerald' ? 'var(--gg-emerald)' : 'var(--gg-info)' }}>
        <Icon size={18} />
      </span>
      <div>
        <p className="gg-eyebrow">{label}</p>
        <p className="gg-kpi-value gg-tabular mt-1" style={{ fontSize: 26 }}>{value}</p>
        <p className="text-xs mt-1" style={{ color: 'var(--gg-text-dim)' }}>{sub}</p>
      </div>
    </div>
  </motion.button>
);

const SummaryCard = ({ icon: Icon, tone, label, value }) => (
  <div className="gg-card p-5">
    <div className="flex items-start gap-3">
      <span className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0`} style={{ background: `var(--gg-${tone}-soft)`, color: `var(--gg-${tone})` }}>
        <Icon size={18} />
      </span>
      <div>
        <p className="gg-eyebrow">{label}</p>
        <p className="gg-kpi-value gg-tabular mt-1" style={{ fontSize: 26 }}>{value}</p>
      </div>
    </div>
  </div>
);
